// invariantsFor / sectionsFor — the ONE place a composer kind adds blocks to
// the generic <CompositionWorkbench>.
//
// Four of the seven composers do. SCRIVENER holds the letter invariants
// across its three tonal variants; HERALD lists the pack documents; FORUM
// shows the RAG matrix and the decisions still pending; ARBITER shows the
// fair-value verdict. EMISSARY, CHARTER and HAVEN add nothing, which is why
// their wrapper files no longer exist.
//
// Each reader narrows the draft itself and renders nothing when the block is
// absent, so the workbench stays generic over `BaseDraft` and the page needs
// no per-kind component.

import type { ReactNode } from 'react';

import type { WorkbenchKind } from '@septima/contract';
import { cn } from '@/lib/utils';

function stringAt(draft: unknown, key: string): string | null {
    if (typeof draft !== 'object' || draft === null) return null;
    const value = (draft as Record<string, unknown>)[key];
    return typeof value === 'string' && value.length > 0 ? value : null;
}

function stringsAt(draft: unknown, key: string): string[] {
    if (typeof draft !== 'object' || draft === null) return [];
    const value = (draft as Record<string, unknown>)[key];
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is string => typeof item === 'string');
}

function rowsAt<T>(draft: unknown, key: string, narrow: (row: unknown) => T | null): T[] {
    if (typeof draft !== 'object' || draft === null) return [];
    const value = (draft as Record<string, unknown>)[key];
    if (!Array.isArray(value)) return [];
    const out: T[] = [];
    for (const row of value) {
        const narrowed = narrow(row);
        if (narrowed) out.push(narrowed);
    }
    return out;
}

const PANEL_CLS =
    'rounded-md border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-secondary)/0.4)] p-3';
const TITLE_CLS = 'text-card-title text-[rgb(var(--color-text-primary))]';
const BODY_CLS = 'text-annotation text-[rgb(var(--color-text-secondary))]';

function BulletList({ label, items }: { label: string; items: string[] }) {
    if (items.length === 0) return null;
    return (
        <div>
            <div className={cn(TITLE_CLS, 'mb-1')}>{label}</div>
            <ul className="list-disc ml-5 flex flex-col gap-0.5">
                {items.map((item, i) => (
                    <li key={i} className={BODY_CLS}>
                        {item}
                    </li>
                ))}
            </ul>
        </div>
    );
}

// -------- SCRIVENER — the letter invariants held across the variants --------

function ScrivenerInvariants({ draft }: { draft: unknown }) {
    const summary = stringAt(draft, 'summary');
    const driftDetails = stringAt(draft, 'driftDetails');
    const feeDisclosure = stringAt(draft, 'feeDisclosure');
    return (
        <div data-testid="scrivener-invariants" className={cn(PANEL_CLS, 'flex flex-col gap-2.5')}>
            <div className={TITLE_CLS}>Letter invariants (held across variants)</div>
            {summary && (
                <p className="text-annotation text-[rgb(var(--color-text-primary))]">{summary}</p>
            )}
            {driftDetails && <p className={BODY_CLS}>{driftDetails}</p>}
            <BulletList label="Recommendations" items={stringsAt(draft, 'recommendations')} />
            <BulletList label="Risk warnings" items={stringsAt(draft, 'riskWarnings')} />
            {feeDisclosure && (
                <div>
                    <div className={cn(TITLE_CLS, 'mb-1')}>Fee disclosure</div>
                    <p className={BODY_CLS}>{feeDisclosure}</p>
                </div>
            )}
        </div>
    );
}

// -------- HERALD — the canonical pack documents --------

interface PackDocument {
    kind: string;
    title: string;
}

function packDocument(row: unknown): PackDocument | null {
    if (typeof row !== 'object' || row === null) return null;
    const { kind, title } = row as Partial<PackDocument>;
    if (typeof kind !== 'string' || typeof title !== 'string') return null;
    return { kind, title };
}

function HeraldSections({ draft }: { draft: unknown }) {
    const documents = rowsAt(draft, 'pack_documents', packDocument);
    if (documents.length === 0) return null;
    return (
        <div data-testid="herald-pack-documents" className={PANEL_CLS}>
            <div className={cn(TITLE_CLS, 'mb-2')}>Pack documents</div>
            <ul className="flex flex-col gap-1">
                {documents.map((doc, i) => (
                    <li key={i} className={BODY_CLS}>
                        <span className="font-mono text-[rgb(var(--color-text-primary))]">
                            [{doc.kind}]
                        </span>{' '}
                        {doc.title}
                    </li>
                ))}
            </ul>
        </div>
    );
}

// -------- FORUM — the RAG matrix and the decisions pending --------

const RAG_CLS: Record<string, string> = {
    Green: 'bg-[rgb(var(--color-rag-green)/0.15)] text-[rgb(var(--color-rag-green))]',
    Amber: 'bg-[rgb(var(--color-rag-amber)/0.15)] text-[rgb(var(--color-rag-amber))]',
    Red: 'bg-[rgb(var(--color-rag-red)/0.15)] text-[rgb(var(--color-rag-red))]',
};

interface RagRow {
    area: string;
    rag: string;
}

function ragRow(row: unknown): RagRow | null {
    if (typeof row !== 'object' || row === null) return null;
    const { area, rag } = row as Partial<RagRow>;
    if (typeof area !== 'string' || typeof rag !== 'string') return null;
    return { area, rag };
}

function ForumSections({ draft }: { draft: unknown }) {
    const rag = rowsAt(draft, 'rag_status', ragRow);
    const decisions = stringsAt(draft, 'decisions_pending');
    if (rag.length === 0 && decisions.length === 0) return null;
    return (
        <div className="flex flex-col gap-2">
            {rag.length > 0 && (
                <div data-testid="forum-rag-matrix" className={PANEL_CLS}>
                    <div className={cn(TITLE_CLS, 'mb-2')}>RAG status</div>
                    <div className="flex flex-wrap gap-1.5">
                        {rag.map((row, i) => (
                            <span
                                key={i}
                                className={cn(
                                    'text-annotation px-2 py-0.5 rounded-button',
                                    RAG_CLS[row.rag],
                                )}
                            >
                                {row.area}: {row.rag}
                            </span>
                        ))}
                    </div>
                </div>
            )}
            {decisions.length > 0 && (
                <div className={PANEL_CLS}>
                    <div className={cn(TITLE_CLS, 'mb-2')}>Decisions pending</div>
                    <ul className="list-disc ml-5 flex flex-col gap-0.5">
                        {decisions.map((decision, i) => (
                            <li key={i} className={BODY_CLS}>
                                {decision}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

// -------- ARBITER — the computed fair-value verdict --------

const VERDICT_META: Record<string, { label: string; cls: string }> = {
    fair_value: {
        label: 'Fair value',
        cls: 'bg-[rgb(var(--color-rag-green)/0.15)] text-[rgb(var(--color-rag-green))]',
    },
    not_fair_value: {
        label: 'Not fair value',
        cls: 'bg-[rgb(var(--color-rag-red)/0.15)] text-[rgb(var(--color-rag-red))]',
    },
    review_required: {
        label: 'Review required',
        cls: 'bg-[rgb(var(--color-rag-amber)/0.15)] text-[rgb(var(--color-rag-amber))]',
    },
};

function ArbiterSections({ draft }: { draft: unknown }) {
    const verdict = stringAt(draft, 'verdict');
    const meta = verdict ? VERDICT_META[verdict] : undefined;
    if (!meta) return null;
    return (
        <div data-testid="arbiter-verdict" className="flex items-center gap-2">
            <span className={BODY_CLS}>Fair-value verdict</span>
            <span
                className={cn('text-annotation px-2 py-0.5 rounded-button font-medium', meta.cls)}
            >
                {meta.label}
            </span>
        </div>
    );
}

// -------- The two lookups the workbench reads --------

/** The invariants block of a composer kind, or null when it has none. */
export function invariantsFor(kind: WorkbenchKind): ((draft: unknown) => ReactNode) | null {
    if (kind === 'suitability-letter-draft') {
        return (draft) => <ScrivenerInvariants draft={draft} />;
    }
    return null;
}

/** The agent-specific section list of a composer kind, or null when it has none. */
export function sectionsFor(kind: WorkbenchKind): ((draft: unknown) => ReactNode) | null {
    if (kind === 'onboarding-pack-draft') return (draft) => <HeraldSections draft={draft} />;
    if (kind === 'governance-paper-draft') return (draft) => <ForumSections draft={draft} />;
    if (kind === 'fair-value-assessment-draft') return (draft) => <ArbiterSections draft={draft} />;
    return null;
}
